import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { AlertCircle, CheckCircle, Loader2 } from "lucide-react";

const formatScore = (value) =>
  value === null || value === undefined ? "—" : `${value.toFixed(1)}%`;

const statusVariant = (status) => {
  switch (status) {
    case "completed":
      return "bg-emerald-100 text-emerald-700";
    case "failed":
      return "bg-red-100 text-red-700";
    default:
      return "bg-slate-100 text-slate-700";
  }
};

const StudentGrades = () => {
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchGrades = async () => {
      try {
        const response = await axios.get(`${API}/users/my-grades`);
        setGrades(response.data);
      } catch (err) {
        setError("No pudimos cargar tus calificaciones. Intenta nuevamente.");
      } finally {
        setLoading(false);
      }
    };

    fetchGrades();
  }, []);

  return (
    <div className="space-y-6" data-testid="student-grades-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 font-heading">Mis Calificaciones</h1>
        <p className="text-slate-600 mt-1">Revisa el progreso obtenido en cada uno de tus cursos.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Resumen por curso</CardTitle>
        </CardHeader>
        <CardContent>
          {loading && (
            <div className="flex items-center justify-center py-12 text-slate-500">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Cargando calificaciones...
            </div>
          )}

          {!loading && error && (
            <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && grades.length === 0 && (
            <div className="flex flex-col items-center gap-3 py-12 text-center text-slate-500">
              <CheckCircle className="w-8 h-8 text-emerald-500" />
              <p>No tienes cursos inscritos todavía.</p>
            </div>
          )}

          {!loading && !error && grades.length > 0 && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Curso</TableHead>
                    <TableHead>Calificación Final</TableHead>
                    <TableHead>Pretest</TableHead>
                    <TableHead>Postest</TableHead>
                    <TableHead>Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {grades.map((grade) => (
                    <TableRow key={grade.course_id}>
                      <TableCell className="font-medium text-slate-900">{grade.course_name}</TableCell>
                      <TableCell className="text-lg font-semibold text-slate-900">
                        {grade.grade !== null && grade.grade !== undefined
                          ? `${grade.grade.toFixed(1)}%`
                          : "—"}
                      </TableCell>
                      <TableCell>{formatScore(grade.pretest_score)}</TableCell>
                      <TableCell>{formatScore(grade.posttest_score)}</TableCell>
                      <TableCell>
                        <Badge className={statusVariant(grade.status)}>
                          {grade.status === "completed"
                            ? "Completado"
                            : grade.status === "failed"
                            ? "Reprobado"
                            : "En progreso"}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StudentGrades;
